import { Injectable } from '@angular/core';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Patient } from '@models/patient';

@Injectable({
  providedIn: 'root',
})
/**
 * Abstract base class for storage services.
 */
export abstract class AbstractStorageService {
  private readonly PATIENT_STORAGE_KEY = 'patient';
  private readonly TRANSACTION_ID_STORAGE_KEY = 'transaction_id';
  private readonly TREATMENT_TYPE_STORAGE_KEY = 'treatmentType';
  private readonly UPSELL_STORAGE_KEY = 'upsell_transaction_id';

  /**
   * Gets the patient data from storage.
   *
   * @returns {Patient} the stored patient object
   */
  public get patient(): Patient | null {
    return this.get(this.PATIENT_STORAGE_KEY);
  }

  /**
   * Gets the transaction ID from storage.
   *
   * @returns {string} the stored transaction ID or empty string if not found
   */
  public get transactionId(): string {
    return this.get(this.TRANSACTION_ID_STORAGE_KEY, false) ?? '';
  }

  /**
   * Gets the treatment type from storage.
   *
   * @returns {ConsultationTreatmentTypes} the stored treatment type
   */
  public get treatmentType(): ConsultationTreatmentTypes | null {
    return this.get(this.TREATMENT_TYPE_STORAGE_KEY);
  }

  /**
   * Gets the upsell transaction IDs from storage.
   *
   * @returns {string[]} the stored upsell transaction IDs or empty array if not found
   */
  public get upsell(): string[] {
    return this.get(this.UPSELL_STORAGE_KEY, false) ?? [];
  }

  /**
   * Sets the patient data in storage.
   *
   * @param {Patient} patient the patient object to store
   */
  public set patient(patient: Patient) {
    this.set(this.PATIENT_STORAGE_KEY, patient);
  }

  /**
   * Sets the transaction ID in storage.
   *
   * @param {string} transactionId the transaction ID to store
   */
  public set transactionId(transactionId: string) {
    this.set(this.TRANSACTION_ID_STORAGE_KEY, transactionId, false);
  }

  /**
   * Sets the treatment type in storage.
   *
   * @param {ConsultationTreatmentTypes} treatmentType the treatment type to store
   */
  public set treatmentType(treatmentType: ConsultationTreatmentTypes) {
    this.set(this.TREATMENT_TYPE_STORAGE_KEY, treatmentType);
  }

  /**
   * Sets the upsell transaction IDs in storage.
   *
   * @param {string[]} upsell the array of upsell transaction IDs to store
   */
  public set upsell(upsell: string[]) {
    this.set(this.UPSELL_STORAGE_KEY, upsell, false);
  }

  /**
   * Gets the full key to be used in storage.
   *
   * @param {string} key the key to be used in storage
   * @param {boolean} usePrefix whether to use the prefix or not
   *
   * @returns {string} the full key to be used in storage
   */
  protected getFullKey(key: string, usePrefix: boolean): string {
    return `${usePrefix ? 'ngStorage-' : ''}${key}`;
  }

  /**
   * Attempts to parse a JSON string value, returning the original string if parsing fails due to invalid JSON syntax.
   *
   * @param {string} value the string value to parse as JSON
   *
   * @returns {any} the parsed JSON object or original string if parsing fails
   */
  protected jsonParseValue(value: string): any {
    try {
      return JSON.parse(value);
    } catch (error) {
      if (!(error instanceof SyntaxError)) {
        throw error;
      }
    }

    return value;
  }

  /**
   * Converts a value to its string representation, returning the value as-is if it's already a string, otherwise JSON
   * stringifying it.
   *
   * @param {any} value the value to convert to string
   *
   * @returns {string} the string representation of the value
   */
  protected stringifyValue(value: any): string {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  /**
   * Removes an item from storage by key.
   *
   * @param {string} key the key of the item to remove
   * @param {boolean} usePrefix whether to use the prefix or not
   */
  public abstract remove(key: string, usePrefix?: boolean): void;

  /**
   * Retrieves an item from storage by key.
   *
   * @param {string} key the key of the item to retrieve
   * @param {boolean} usePrefix whether to use the prefix or not
   *
   * @returns {any} the retrieved value or null if not found
   */
  protected abstract get(key: string, usePrefix?: boolean): any;

  /**
   * Stores an item in storage with the specified key and value.
   *
   * @param {string} key the key to store the value under
   * @param {any} value the value to store
   * @param {boolean} usePrefix whether to use the prefix or not
   */
  protected abstract set(key: string, value: any, usePrefix?: boolean): void;
}
